import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "../lib/apiClient";
import type {
  ApproveBudgetResponse,
  AssignCardHolderPayload,
  AssignCardHolderResponse,
  CreateBudgetRequest,
  CreateBudgetResponse,
  ReturnBudgetPayload,
  ReturnBudgetResponse,
} from "../schemas/budget";
import { BUDGET_QUERY_KEYS } from "./useBudgetQueries";

/**
 * UC-002 budget admin mutations (Phase 16).
 *
 * 3 mutace: approve (POST /approve), return (POST /return-to-draft),
 * resolveUnassigned (POST /resolve-unassigned-spz). Cache invalidation
 * vyhazuje list + detail + unassigned + plate-candidates dotčeného rozpočtu.
 *
 * Approve obal — princip #2 immutable snapshot; backend vrací 409
 * BUDGET_ALREADY_APPROVED pokud second-click race. UI mutation hook
 * předává `RateLimitedError` / `ApiError` k caller pro toast render.
 */

export function useCreateBudget() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateBudgetRequest) =>
      apiCall<CreateBudgetResponse>("/api/admin/budgets", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: (data) => {
      void client.invalidateQueries({ queryKey: BUDGET_QUERY_KEYS.list });
      void client.invalidateQueries({
        queryKey: BUDGET_QUERY_KEYS.availableTargets,
      });
      void client.invalidateQueries({
        queryKey: BUDGET_QUERY_KEYS.detail(data.budget_id),
      });
    },
  });
}

export function useApproveBudget() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (budgetId: string) =>
      apiCall<ApproveBudgetResponse>(
        `/api/admin/budgets/${budgetId}/approve`,
        { method: "POST", body: JSON.stringify({}) },
      ),
    onSuccess: (_, budgetId) => {
      void client.invalidateQueries({ queryKey: BUDGET_QUERY_KEYS.list });
      void client.invalidateQueries({
        queryKey: BUDGET_QUERY_KEYS.detail(budgetId),
      });
      void client.invalidateQueries({
        queryKey: BUDGET_QUERY_KEYS.unassigned(budgetId),
      });
    },
  });
}

export function useReturnBudget() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      budgetId,
      payload,
    }: {
      budgetId: string;
      payload: ReturnBudgetPayload;
    }) =>
      apiCall<ReturnBudgetResponse>(
        `/api/admin/budgets/${budgetId}/return-to-draft`,
        { method: "POST", body: JSON.stringify(payload) },
      ),
    onSuccess: (_, { budgetId }) => {
      void client.invalidateQueries({ queryKey: BUDGET_QUERY_KEYS.list });
      void client.invalidateQueries({
        queryKey: BUDGET_QUERY_KEYS.detail(budgetId),
      });
    },
  });
}

export function useAssignCardHolder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      budgetId,
      payload,
    }: {
      budgetId: string;
      payload: AssignCardHolderPayload;
    }) =>
      apiCall<AssignCardHolderResponse>(
        `/api/admin/budgets/${budgetId}/assign-card-holder`,
        { method: "POST", body: JSON.stringify(payload) },
      ),
    onSuccess: (_, { budgetId }) => {
      void client.invalidateQueries({
        queryKey: BUDGET_QUERY_KEYS.unassigned(budgetId),
      });
      void client.invalidateQueries({
        queryKey: BUDGET_QUERY_KEYS.detail(budgetId),
      });
      void client.invalidateQueries({ queryKey: BUDGET_QUERY_KEYS.list });
    },
  });
}
